import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { NodeEditor } from 'modules/workflows/components/NodeEditor';
import {
  WorkflowsRootState,
  fetchFbCustomAudienceRequest,
  getFbAccountIds,
  getFbCustomAudienceByAccountId,
  getIsFetchingFbCustomAudience,
  getIsNodeEditorReadOnly,
  updateNodeData,
} from 'modules/workflows/ducks';
import { FbCustomAudience, FbCustomAudienceNodeData } from 'modules/workflows/types';
import { useDisplayCloneErrors } from 'modules/workflows/hooks/useDisplayCloneErrors';
import { FormField } from 'ui/formField';
import { SelectValueType, BasicFilterSelect, BasicSelect } from 'ui/selects';

import {
  StyledLeftTooltip,
  HalfPaddedFormSectionContent,
} from 'modules/workflowNodeEditors/EditorBody.styled';
import { EditorBaseProps } from '../../types';
import {
  StyledBanner,
  StyledFormLabel,
  StyledRadioList,
} from './FacebookCustomAudienceEditor.styles';
import {
  ACCOUNT_DROPDOWN_ID,
  CUSTOM_AUDIENCE_ID,
  EDITOR_TEST_ID,
  FB_ACTION_OPTIONS,
  DEFAULT_FACEBOOK_CUSTOM_AUDIENCE_NODE_DATA,
} from './constants';
import {
  getAccountIdDropdownOptions,
  getCustomAudienceDropdownOptions,
  getAudienceLabel,
} from './utils';

export type FacebookCustomAudienceEditorProps = EditorBaseProps<FbCustomAudienceNodeData>;

export const FacebookCustomAudienceEditor = ({
  nodeData = DEFAULT_FACEBOOK_CUSTOM_AUDIENCE_NODE_DATA,
  clonedFromNodeData = DEFAULT_FACEBOOK_CUSTOM_AUDIENCE_NODE_DATA,
  nodeId,
  ...rest
}: FacebookCustomAudienceEditorProps) => {
  const dispatch = useDispatch();
  const { adAccountId, customAudience, nodeClass } = nodeData;
  const { adAccountId: clonedAccountId, customAudience: clonedAudience } = clonedFromNodeData;
  const selectedAction = FB_ACTION_OPTIONS.find((option) => option.value === nodeClass);

  const [selectedAccountId, setSelectedAccountId] = useState(adAccountId);
  const [selectedAudience, setSelectedAudience] = useState(customAudience);
  const [fbAction, setFbAction] = useState(selectedAction?.value || FB_ACTION_OPTIONS[0].value);
  const [showValidation, setShowValidation] = useState({
    adAccountId: false,
    customAudience: false,
  });

  const isTileEditorReadOnly = useSelector(getIsNodeEditorReadOnly);
  const isFetching = useSelector(getIsFetchingFbCustomAudience);
  const accountIds = useSelector(getFbAccountIds);
  const customAudiences = useSelector((state: WorkflowsRootState) =>
    getFbCustomAudienceByAccountId(state, selectedAccountId),
  );

  useEffect(() => {
    dispatch(fetchFbCustomAudienceRequest());
  }, []);

  useEffect(() => {
    setShowValidation({ adAccountId: !!clonedAccountId, customAudience: !!clonedAudience });
  }, [clonedAccountId, clonedAudience]);

  const handleUpdate = () => {
    dispatch(
      updateNodeData(
        {
          ...nodeData,
          nodeClass: fbAction,
          adAccountId: selectedAccountId,
          customAudience: selectedAudience,
        },
        nodeId,
      ),
    );
  };

  const handleSelectAccountId = ([val]: SelectValueType[]) => {
    if (val !== selectedAccountId) {
      // reset Audience dropdown if a different account ID is selected
      setSelectedAudience(undefined);
    }
    setSelectedAccountId(val as string);
    setShowValidation({ ...showValidation, adAccountId: false });
  };

  const {
    isCloneWarningBannerShown,
    cloneErrors = {},
    cloneDisplayValues = {},
  } = useDisplayCloneErrors({
    clonedFromNodeData,
    fieldsToValidate: {
      adAccountId: {
        isValid: !showValidation.adAccountId,
      },
      customAudience: {
        isValid: !showValidation.customAudience,
        displayValue: getAudienceLabel(customAudience?.customAudienceId, customAudience?.name),
      },
    },
    nodeClass: fbAction,
  });

  return (
    <NodeEditor
      {...rest}
      data-test={EDITOR_TEST_ID}
      onUpdate={handleUpdate}
      isCloneWarningBannerShown={isCloneWarningBannerShown}
    >
      <form>
        <FormField>
          <StyledFormLabel>Action</StyledFormLabel>
          <StyledRadioList
            name="set-sub-action"
            onSelect={setFbAction}
            options={FB_ACTION_OPTIONS}
            value={fbAction}
            isReadOnly={isTileEditorReadOnly}
          />
        </FormField>
        <FormField>
          <StyledFormLabel>Facebook Custom Audience</StyledFormLabel>
          <HalfPaddedFormSectionContent>
            <StyledLeftTooltip isEnabled={isTileEditorReadOnly} tooltipContent={selectedAccountId}>
              <BasicSelect
                id={ACCOUNT_DROPDOWN_ID}
                onSelect={handleSelectAccountId}
                options={getAccountIdDropdownOptions(accountIds)}
                placeholder={isFetching ? 'Loading...' : 'Facebook Ads account ID...'}
                isError={showValidation.adAccountId}
                isDisabled={isTileEditorReadOnly}
                {...(adAccountId && { defaultValues: [adAccountId] })}
                isFullWidth
              />
            </StyledLeftTooltip>
          </HalfPaddedFormSectionContent>
          <StyledLeftTooltip
            isEnabled={isTileEditorReadOnly}
            tooltipContent={`${selectedAudience?.customAudienceId} - ${selectedAudience?.name}`}
          >
            <BasicFilterSelect
              id={CUSTOM_AUDIENCE_ID}
              onSelect={(_, { selectedOptions }) => {
                if (selectedOptions.length) {
                  setSelectedAudience(selectedOptions[0].data as FbCustomAudience);
                  setShowValidation({ ...showValidation, customAudience: false });
                }
              }}
              options={getCustomAudienceDropdownOptions(customAudiences)}
              placeholder={
                cloneErrors.customAudience
                  ? cloneDisplayValues.customAudience
                  : 'Facebook custom audience...'
              }
              inputContent={{
                filterPlaceholder: 'Search custom audiences',
              }}
              isDisabled={isTileEditorReadOnly}
              isError={showValidation.customAudience}
              {...(selectedAudience &&
                !cloneErrors.customAudience && {
                  defaultValues: [selectedAudience.customAudienceId],
                })}
              isFullWidth
            />
          </StyledLeftTooltip>
        </FormField>
      </form>
      <StyledBanner>
        <a
          href="https://support.iterable.com/hc/articles/115005019663"
          target="_blank"
          rel="noreferrer noopener"
        >
          Learn more
        </a>{' '}
        about connecting your Facebook Ads account.
      </StyledBanner>
    </NodeEditor>
  );
};
